import {Dayjs} from 'dayjs';
import {parseAgendaDate} from '@/agenda/lib/agendaTime';

export type AgendaViewKey = 'day' | 'week' | 'month' | 'team';

export interface AgendaRange {
    from: Date;
    to: Date;
}

/**
 * Days of the pharmacy week, Monday first. Sunday is added by the week view
 * only when it actually holds an appointment (on-call duty), so a closed
 * Sunday costs no column but an open one is never hidden.
 */
export const WEEK_DAYS_WITHOUT_SUNDAY = 6;

/**
 * Window to fetch for a view. The month covers the whole rendered calendar,
 * leading and trailing days included, so the day counters are exact.
 */
export const rangeForView = (view: AgendaViewKey, day: Date): AgendaRange => {
    const anchor = parseAgendaDate(day);

    if (view === 'week') {
        return {from: anchor.startOf('week').toDate(), to: anchor.endOf('week').toDate()};
    }

    if (view === 'month') {
        return {
            from: anchor.startOf('month').startOf('week').toDate(),
            to: anchor.endOf('month').endOf('week').toDate()
        };
    }

    return {from: anchor.startOf('day').toDate(), to: anchor.endOf('day').toDate()};
};

/**
 * Move the anchor date by one unit of the current view.
 */
export const shiftForView = (view: AgendaViewKey, day: Date, direction: number): Date => {
    const unit = view === 'week' ? 'week' : view === 'month' ? 'month' : 'day';

    return parseAgendaDate(day).add(direction, unit).toDate();
};

export interface DatedAppointment {
    start_at: string;
    end_at: string;
}

export interface DatedReminder {
    reminder_at: string;
}

/**
 * Whether a reminder is sent on the given day. A reminder is a single moment,
 * not an interval, so it belongs to exactly one column — and this is the only
 * predicate deciding it, so the column count and the column contents cannot
 * disagree.
 */
export const fallsOnDay = (reminder: DatedReminder, day: Dayjs): boolean => {
    return parseAgendaDate(reminder.reminder_at).isSame(day, 'day');
};

/**
 * Whether an appointment occupies any minute of the given day. Used both to
 * fill a column and to decide whether Sunday deserves one, so a night shift
 * cannot end up with a segment and no column to draw it in.
 */
export const overlapsDay = (appointment: DatedAppointment, day: Dayjs): boolean => {
    return parseAgendaDate(appointment.start_at).isBefore(day.endOf('day'))
        && parseAgendaDate(appointment.end_at).isAfter(day.startOf('day'));
};

/**
 * Columns the week view shows: Sunday is only worth a column when something
 * actually lands on it — an appointment (on-call duty) or a renewal reminder,
 * which the daily cron sends on Sundays too. Leaving the reminders out of the
 * count made those invisible: the band draws them per column, so a reminder
 * without a column has nowhere to go. The heading derives from the same count,
 * so the two can never disagree.
 */
export const weekColumnCount = (
    appointments: DatedAppointment[],
    day: Date,
    reminders: DatedReminder[] = []
): number => {
    const sunday = parseAgendaDate(day).startOf('week').add(WEEK_DAYS_WITHOUT_SUNDAY, 'day');

    const sundayIsBusy = appointments.some((appointment) => overlapsDay(appointment, sunday))
        || reminders.some((reminder) => fallsOnDay(reminder, sunday));

    return sundayIsBusy ? 7 : WEEK_DAYS_WITHOUT_SUNDAY;
};

/**
 * Heading describing the displayed window.
 */
export const labelForView = (
    view: AgendaViewKey,
    day: Date,
    weekColumns: number = WEEK_DAYS_WITHOUT_SUNDAY
): string => {
    const anchor = parseAgendaDate(day);

    if (view === 'week') {
        const start = anchor.startOf('week');
        const end = start.add(weekColumns - 1, 'day');
        const sameMonth = start.isSame(end, 'month');

        return `${start.format(sameMonth ? 'D' : 'D MMMM')} – ${end.format('D MMMM YYYY')}`;
    }

    if (view === 'month') {
        return anchor.format('MMMM YYYY');
    }

    return anchor.format('dddd D MMMM YYYY');
};

/**
 * Calendar weeks of the month view, each as its own days.
 */
export const monthWeeks = (day: Date): Dayjs[][] => {
    const start = parseAgendaDate(day).startOf('month').startOf('week');
    const end = parseAgendaDate(day).endOf('month').endOf('week');
    const weeks: Dayjs[][] = [];

    let cursor = start;

    while (cursor.isBefore(end)) {
        weeks.push(Array.from({length: 7}, (_, index) => cursor.add(index, 'day')));
        cursor = cursor.add(7, 'day');
    }

    return weeks;
};
